import React, { useState, useEffect } from 'react'
import { formatLocal } from 'utils/date'

import { translate } from 'locales'

import { Title, Loading } from 'components'
import noData from 'assets/images/no-data.svg'
import regenerateIcon from 'assets/icons/mage_stars-c.svg'

import useFetch from 'hooks/useFetch'
import useStorage from 'hooks/storageSync'
import { alertSuccess, alertDanger } from 'utils/toast'
import {
  checkPlanSentStatus,
  sendMealPlan,
  regenerateMealPlan,
  generateMealPlanPDF,
} from './services'

// Styles
import * as S from './styles'

// Interfaces
import { IComponentProps } from './interfaces'
import { MenuAlimentarResponse, Refeicao, OpcaoAlimentar } from './types'

import FoodMenuEdit from './PlanFoodMenuEdit'

const FoodMenu: React.FunctionComponent<IComponentProps> = ({
  doctorName,
  patientId,
  scheduleUUID,
  doctorId,
  planSent,
}) => {
  const [edit, setEdit] = useState(false)
  const [menuData, setMenuData] = useState<MenuAlimentarResponse | null>(null)
  const [isPlanValidated, setIsPlanValidated] = useState(false)
  const [isPlanSent, setIsPlanSent] = useState(false)
  const [isCheckingSentStatus, setIsCheckingSentStatus] = useState(true)
  const [isMenuModified, setIsMenuModified] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [justRegenerated, setJustRegenerated] = useState(false)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const [storage, , setAllStorage] = useStorage(
    scheduleUUID,
    'nutritional_anamnese'
  )

  const { isLoading } = useFetch({
    url: `/stela-meal/v1/plan/user/${patientId}`,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const { data: appointment } = useFetch({
    url: `/schedulings/ms/v1/${scheduleUUID}/doctor`,
    condition: !!scheduleUUID,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const { data: userData } = useFetch({
    url: `accounts/ms/v1/user/${appointment?.user_id}/me`,
    condition: !!appointment?.user_id,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const { data: userMedical } = useFetch({
    url: `accounts/v1/medical-record/user/${appointment?.user_id}`,
    condition: !!appointment?.user_id,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const convertApiFormatToMenuData = (
    apiData: any
  ): MenuAlimentarResponse | null => {
    if (!apiData || !apiData.menu) return null

    // O formato da API já está quase correto, só precisa ajustar a estrutura
    const converted: MenuAlimentarResponse = {
      success: true,
      menu: {
        ...apiData.menu,
        // Garantir que os campos opcionais existam
        receitas: apiData.menu.receitas || [],
        adaptacoes_realizadas: apiData.menu.adaptacoes_realizadas || [],
        incluso_plano: apiData.menu.incluso_plano || {
          orientacoes: [],
          observacoes: [],
          dicas_planejamento: null,
        },
      },
      metadados: apiData.metadata || {},
      caracteristicas_anamnese: apiData.anamnese || {},
    }

    return converted
  }

  const checkPlanSentStatusLocal = async () => {
    try {
      setIsCheckingSentStatus(true)
      const result = await checkPlanSentStatus(scheduleUUID)

      if (result.isSent && result.data) {
        setIsPlanSent(true)
        setIsPlanValidated(true)

        if (result.data.modified) {
          setIsMenuModified(true)
        }

        const convertedData = convertApiFormatToMenuData(result.data)
        if (convertedData) {
          setMenuData(convertedData)
        }
      } else {
        setIsPlanSent(false)
      }
    } catch {
      setIsPlanSent(false)
    } finally {
      setIsCheckingSentStatus(false)
    }
  }

  const convertNewFormatToOld = (
    newPlan: any,
    anamneseData?: any
  ): MenuAlimentarResponse | null => {
    if (!newPlan) return null

    if (newPlan.menu && newPlan.menu.orientacoes_nutricionais) {
      return newPlan
    }

    const plan = newPlan.plan || newPlan

    if (!plan) return null

    let mealsArray: any[] = []
    if (plan.meals) {
      if (Array.isArray(plan.meals)) {
        mealsArray = plan.meals
      } else if (typeof plan.meals === 'object') {
        mealsArray = Object.values(plan.meals)
      }
    }

    const refeicoes = mealsArray.map((meal: any) => ({
      tipo: meal.type || '',
      titulo: meal.name || '',
      instrucoes: meal.target_kcal
        ? `${Math.round(meal.target_kcal)} kcal`
        : '',
      opcoes:
        meal.items?.map((item: any) => ({
          descricao: item.description || item.name || '',
          porcao: item.portion || '',
          alternativas: Array.isArray(item.alternatives)
            ? item.alternatives
            : [],
          observacoes: item.notes || null,
          opcional: item.is_optional || false,
        })) || [],
    }))

    const guidelines = plan.nutritional_guidelines_detailed || {}
    const orientacoes_nutricionais = {
      nome: guidelines.patient_name || plan.user_id || '',
      data: plan.created_at || new Date().toISOString().split('T')[0],
      objetivo:
        guidelines.objective ||
        plan.explanations?.rationale ||
        plan.personalization_notes ||
        '',
      liquidos: guidelines.fluids || '',
      suplementos: guidelines.supplements || '',
      proxima_consulta:
        guidelines.next_appointment ||
        plan.nutritional_guidelines_detailed?.next_appointment ||
        '',
    }

    const incluso_plano = {
      orientacoes: Array.isArray(plan.nutritional_guidelines)
        ? plan.nutritional_guidelines
        : [],
      observacoes: Array.isArray(plan.safety?.clinical_warnings)
        ? plan.safety.clinical_warnings
        : [],
      dicas_planejamento:
        plan.personalization_notes || plan.explanations?.how_to_follow || null,
    }

    const metadata = newPlan.metadata || {}
    const anamnese = anamneseData || {}

    const template = plan.template || {}
    const metadados = {
      execution_time: metadata.execution_time || metadata.processing_time || 0,
      categoria_selecionada:
        template.category || metadata.template_used?.category || '',
      metodo_selecao:
        template.selection_method ||
        metadata.template_used?.selection_method ||
        '',
      justificativa_selecao:
        template.selection_reasoning ||
        metadata.template_used?.selection_reasoning ||
        '',
      adaptacoes_realizadas: Array.isArray(plan.adaptations_summary)
        ? plan.adaptations_summary
        : [],
      timestamp: metadata.timestamp || new Date().toISOString(),
      tempo_ia_selecao: metadata.processing_time || 0,
      timeout_ia: false,
      erro_ia:
        metadata.errors && metadata.errors.length > 0
          ? metadata.errors.join('; ')
          : null,
      paciente: {
        nome: guidelines.patient_name || '',
        peso_atual: anamnese.current_weight || '',
        peso_antigo: anamnese.old_weight || '',
      },
      methodology: metadata.methodology || '',
      version: metadata.version || template.version || '',
      template_id: template.id || '',
      template_category: template.category || '',
      template_selection_method: template.selection_method || '',
      template_selection_reasoning: template.selection_reasoning || '',
    }

    const restricoes: string[] = []
    if (anamnese.allergies === 'Sim' && anamnese.allergies_comments) {
      restricoes.push(
        ...anamnese.allergies_comments
          .split(/[,\n]/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
      )
    }
    if (anamnese.intolerances === 'Sim' && anamnese.intolerances_comments) {
      restricoes.push(
        ...anamnese.intolerances_comments
          .split(/[,\n]/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
      )
    }

    const aversoes: string[] = []
    if (anamnese.food_aversion) {
      aversoes.push(
        ...anamnese.food_aversion
          .split(/[,\n]/)
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
      )
    }

    const caracteristicas_anamnese = {
      nome: guidelines.patient_name || userData?.name || '',
      objetivo: anamnese.reason || [],
      restricoes: restricoes,
      patologias: anamnese.pathologies || [],
      tipo_dieta: anamnese.diet || '',
      atividade_fisica: {
        pratica: anamnese.practice_activity || false,
        descricao: anamnese.practice_activity_comments || '',
      },
      suplementos: anamnese.supplements || [],
      observacoes_suplementos: anamnese.supplements_comments || null,
      medicamentos: {
        usa: anamnese.medicines || false,
        quais: anamnese.medicines_comments || '',
      },
      aversoes: aversoes,
      rotina_alimentar: {
        cafe_manha: anamnese.routine_breakfast || '',
        lanche_manha: anamnese.routine_morning_snack || '',
        almoco: anamnese.routine_lunch || '',
        lanche_tarde: anamnese.routine_afternoon_snack || '',
        jantar: anamnese.routine_dinner || '',
        ceia: anamnese.routine_supper || '',
        fins_de_semana: anamnese.routine_weekends || '',
      },
      consumo_agua: anamnese.water_consumption || '',
      habito_intestinal: {
        frequencia: anamnese.frequency_intestinal_habit || '',
        consistencia: anamnese.consistency_intestinal_habit || '',
      },
      sono: anamnese.sleep || [],
    }

    return {
      success: true,
      menu: {
        template_original: plan.template?.id || '',
        anamnese_id: plan.plan_id || '',
        data_geracao: plan.created_at || new Date().toISOString(),
        orientacoes_nutricionais,
        refeicoes,
        incluso_plano,
        receitas: [],
        adaptacoes_realizadas: Array.isArray(plan.adaptations_summary)
          ? plan.adaptations_summary
          : [],
        observacoes_personalizacao: plan.personalization_notes || '',
        // Novos campos preservados
        daily_energy_kcal:
          plan.daily_energy_kcal || plan.macros?.calories_kcal || null,
        macros: plan.macros
          ? {
              calories_kcal: plan.macros.calories_kcal || 0,
              protein_g: plan.macros.protein_g || 0,
              carbs_g: plan.macros.carbs_g || 0,
              fat_g: plan.macros.fat_g || 0,
            }
          : undefined,
        scientific_metadata: plan.scientific_metadata
          ? {
              bmi_current: plan.scientific_metadata.bmi_current || 0,
              bmi_target: plan.scientific_metadata.bmi_target || 0,
              geb_kcal: plan.scientific_metadata.geb_kcal || 0,
              naf_factor: plan.scientific_metadata.naf_factor || 0,
            }
          : undefined,
        how_to_follow: plan.explanations?.how_to_follow || '',
        regionalization_notes: plan.regionalization_notes || '',
        next_appointment:
          guidelines.next_appointment ||
          plan.nutritional_guidelines_detailed?.next_appointment ||
          '',
      },
      metadados,
      caracteristicas_anamnese,
    }
  }

  useEffect(() => {
    if (isCheckingSentStatus) {
      return
    }

    if (justRegenerated) {
      return
    }

    if (isPlanSent) {
      return
    }

    if (storage?.meal_plan) {
      const plan = storage.meal_plan
      const anamneseData = storage?.nutritional_anamnese

      if (plan.plan && (plan.success === true || plan.success === undefined)) {
        const converted = convertNewFormatToOld(plan, anamneseData)
        setMenuData(converted)
      } else if (plan.meals && !plan.menu) {
        const converted = convertNewFormatToOld({ plan }, anamneseData)
        setMenuData(converted)
      } else if (plan.success === true && plan.menu) {
        setMenuData(plan)
      } else if (plan.success === false) {
        setMenuData(null)
      } else {
        const converted = convertNewFormatToOld(plan, anamneseData)
        setMenuData(converted || plan)
      }
    }

    if (storage?.menu_modified) {
      setIsMenuModified(true)
    }
  }, [
    storage?.meal_plan,
    storage?.menu_modified,
    isPlanSent,
    isCheckingSentStatus,
    justRegenerated,
  ])

  useEffect(() => {
    if (typeof planSent === 'boolean') {
      setIsPlanSent(planSent)
      setIsCheckingSentStatus(false)
      if (planSent) {
        setIsPlanValidated(true)
        if (scheduleUUID) {
          checkPlanSentStatusLocal()
        }
      }
      return
    }
    if (scheduleUUID) {
      checkPlanSentStatusLocal()
    }
  }, [scheduleUUID, planSent])

  const handleSave = async (updatedData: MenuAlimentarResponse) => {
    try {
      setMenuData(updatedData)
      setIsMenuModified(true)

      const updatedStorage = {
        ...storage,
        meal_plan: updatedData,
        plan_updated_at: new Date().toISOString(),
        menu_modified: true,
      }
      setAllStorage(updatedStorage)

      setEdit(false)
    } catch {
      alertDanger(translate('medicalRecord.errorSavingFoodPlan'))
    }
  }

  const handleRegenerate = async (mealType?: string) => {
    if (isRegenerating || isPlanSent || !menuData) return

    setIsRegenerating(true)
    alertSuccess(
      'Regenerando plano alimentar... Isso pode levar alguns segundos.'
    )

    const currentPlan = { ...menuData }

    const result = await regenerateMealPlan({
      anamneseData: storage?.nutritional_anamnese,
      userData,
      userMedical,
      appointment,
      scheduleUUID,
      doctorId,
      convertNewFormatToOld,
      planId:
        menuData?.menu?.anamnese_id || menuData?.metadados?.template_id || '',
      meal: mealType || '',
    })

    if (result.success && result.data) {
      if (mealType && result.data?.menu?.refeicoes) {
        const normalizedMealType = mealType.toLowerCase().replace(/\s+/g, '_')

        if (result.data.menu.refeicoes.length === 1) {
          const regeneratedMeal = result.data.menu.refeicoes[0]

          const updatedRefeicoes = currentPlan.menu.refeicoes.map(
            (refeicao: any) => {
              const refTipo = refeicao.tipo?.toLowerCase().replace(/\s+/g, '_')
              const refTitulo = refeicao.titulo?.toLowerCase() || ''
              const mealTypeLower = mealType.toLowerCase()

              const matches =
                refTipo === normalizedMealType ||
                refTitulo.includes(mealTypeLower) ||
                normalizedMealType.includes(refTipo) ||
                mealTypeLower.includes(refTipo)

              if (matches) {
                return regeneratedMeal
              }
              return refeicao
            }
          )

          const mergedPlan: MenuAlimentarResponse = {
            ...currentPlan,
            menu: {
              ...currentPlan.menu,
              refeicoes: updatedRefeicoes,
            },
            // Preservar metadados e características da anamnese do plano atual
            metadados: currentPlan.metadados || result.data.metadados || {},
            caracteristicas_anamnese:
              currentPlan.caracteristicas_anamnese ||
              result.data.caracteristicas_anamnese ||
              {},
          }

          setMenuData(mergedPlan)
          setJustRegenerated(true)

          const updatedStorage = {
            ...storage,
            meal_plan: mergedPlan,
            plan_updated_at: new Date().toISOString(),
            menu_modified: storage?.menu_modified || false,
          }
          setAllStorage(updatedStorage)

          setTimeout(() => {
            setJustRegenerated(false)
          }, 1000)
        } else {
          const regeneratedMeal = result.data.menu.refeicoes.find(
            (ref: any) => {
              const refTipo = ref.tipo?.toLowerCase().replace(/\s+/g, '_')
              const refTitulo = ref.titulo?.toLowerCase() || ''
              const mealTypeLower = mealType.toLowerCase()

              return (
                refTipo === normalizedMealType ||
                refTitulo.includes(mealTypeLower) ||
                normalizedMealType.includes(refTipo) ||
                mealTypeLower.includes(refTipo)
              )
            }
          )

          if (regeneratedMeal) {
            const updatedRefeicoes = currentPlan.menu.refeicoes.map(
              (refeicao: any) => {
                const refTipo = refeicao.tipo
                  ?.toLowerCase()
                  .replace(/\s+/g, '_')
                const refTitulo = refeicao.titulo?.toLowerCase() || ''
                const mealTypeLower = mealType.toLowerCase()

                const matches =
                  refTipo === normalizedMealType ||
                  refTitulo.includes(mealTypeLower) ||
                  normalizedMealType.includes(refTipo) ||
                  mealTypeLower.includes(refTipo)

                if (matches) {
                  return regeneratedMeal
                }
                return refeicao
              }
            )

            const mergedPlan: MenuAlimentarResponse = {
              ...currentPlan,
              menu: {
                ...currentPlan.menu,
                refeicoes: updatedRefeicoes,
              },
              // Preservar metadados e características da anamnese do plano atual
              metadados: currentPlan.metadados || result.data.metadados || {},
              caracteristicas_anamnese:
                currentPlan.caracteristicas_anamnese ||
                result.data.caracteristicas_anamnese ||
                {},
            }

            setMenuData(mergedPlan)
            setJustRegenerated(true)

            const updatedStorage = {
              ...storage,
              meal_plan: mergedPlan,
              plan_updated_at: new Date().toISOString(),
              menu_modified: storage?.menu_modified || false,
            }
            setAllStorage(updatedStorage)

            setTimeout(() => {
              setJustRegenerated(false)
            }, 1000)
          } else {
            setMenuData(result.data)
          }
        }
      } else {
        setMenuData(result.data)
      }

      if (!mealType || !result.data?.menu?.refeicoes) {
        const updatedStorage = {
          ...storage,
          meal_plan: result.rawData,
          meal_plan_metadata: result.metadata,
          generate_plan_requested: true,
          plan_generated_at: new Date().toISOString(),
          plan_data_structure: 'json_structured',
          plan_patient_id: scheduleUUID,
          menu_modified: storage?.menu_modified || false,
        }
        setAllStorage(updatedStorage)
      }

      alertSuccess('Refeição regenerada com sucesso!')
    }

    setIsRegenerating(false)
  }

  const handleSendPlan = async () => {
    const result = await sendMealPlan(
      menuData,
      patientId,
      doctorId,
      scheduleUUID,
      isMenuModified
    )

    if (result.success) {
      setIsPlanSent(true)
      setIsPlanValidated(true)

      const updatedStorage = {
        ...storage,
        meal_plan_sent: true,
        meal_plan_sent_at: new Date().toISOString(),
      }
      setAllStorage(updatedStorage)
    }
  }

  const handleGeneratePDF = async () => {
    if (!scheduleUUID || isGeneratingPDF) return

    setIsGeneratingPDF(true)
    await generateMealPlanPDF(scheduleUUID)
    setIsGeneratingPDF(false)
  }

  return (
    <S.SectionNutri>
      {(isLoading || isCheckingSentStatus) && (
        <Loading title={translate('medicalRecord.loadingHistoric')} />
      )}
      <>
        <span>{formatLocal(new Date(), 'EEEE, dd/MM/yyyy')}</span>
        <p>{`${translate('medicalRecord.by')} ${doctorName}`}</p>
        {storage?.plan_generated_at && (
          <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            {translate('medicalRecord.planGeneratedAt')}{' '}
            {formatLocal(storage.plan_generated_at, 'dd/MM/yyyy HH:mm')}
          </p>
        )}
      </>

      {!menuData ? (
        <>
          <Title text={translate('medicalRecord.foodMenu')} />

          <S.NoData>
            <img
              src={noData}
              alt="Icone simbolizando que não existe imagens a serem exibidas"
            />
            <span>{translate('medicalRecord.noFoodPlan')}</span>
          </S.NoData>
        </>
      ) : (
        <>
          <div className="headerFoodMenu">
            <Title text={translate('medicalRecord.foodMenu')} />
            <div className="divider" />
            {!edit && !isPlanSent && (
              <button onClick={() => setEdit(true)}>
                {translate('medicalRecord.edit')}
              </button>
            )}

            {isPlanSent && (
              <span
                style={{
                  fontSize: '14px',
                  color: '#10b981',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                ✓ {translate('medicalRecord.planSent')}
              </span>
            )}
          </div>

          {!isPlanSent && isMenuModified && (
            <div
              style={{
                marginTop: '10px',
                marginBottom: '10px',
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  color: '#f59e0b',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                ✏️ {translate('medicalRecord.menuModified')}
              </span>
            </div>
          )}

          <S.ValidationContainer
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div className="checkboxWrapper">
              <input
                type="checkbox"
                id="planValidated"
                className="checkbox"
                checked={isPlanValidated}
                disabled={isPlanSent}
                onChange={(e) => setIsPlanValidated(e.target.checked)}
              />
              <label
                htmlFor="planValidated"
                className="checkboxLabel"
                style={{
                  opacity: isPlanSent ? 0.6 : 1,
                  cursor: isPlanSent ? 'not-allowed' : 'pointer',
                }}
              >
                {translate('medicalRecord.planValidated')}
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isPlanValidated && !isPlanSent && (
                <button className="sendButton" onClick={handleSendPlan}>
                  {translate('medicalRecord.sendFoodPlan')}
                </button>
              )}
              {isPlanSent && (
                <button
                  className="generatePdfButton"
                  onClick={handleGeneratePDF}
                  disabled={isGeneratingPDF}
                >
                  {isGeneratingPDF ? (
                    <>
                      <span>{translate('medicalRecord.downloadingPDF')}</span>
                    </>
                  ) : (
                    <>
                      <span>{translate('medicalRecord.downloadPDF')}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </S.ValidationContainer>

          <div>
            <p className="objective">
              {translate('medicalRecord.foodMenuText')}{' '}
              {storage?.nutritional_anamnese?.reason && (
                <>
                  {' '}
                  {Array.isArray(storage.nutritional_anamnese.reason)
                    ? storage.nutritional_anamnese.reason.join(', ')
                    : storage.nutritional_anamnese.reason}
                </>
              )}
            </p>
          </div>

          {!edit && (
            <>
              {(menuData?.menu?.daily_energy_kcal ||
                menuData?.menu?.macros ||
                menuData?.menu?.scientific_metadata) && (
                <S.NutritionalInfo>
                  <div className="nutritionalInfoHeader">
                    <h3>Informações Nutricionais</h3>
                  </div>
                  <div className="nutritionalInfoGrid">
                    {menuData.menu.daily_energy_kcal && (
                      <div className="nutritionalInfoItem">
                        <div className="nutritionalInfoLabel">
                          Calorias Diárias
                        </div>
                        <div className="nutritionalInfoValue">
                          {Math.round(menuData.menu.daily_energy_kcal)} kcal
                        </div>
                      </div>
                    )}
                    {menuData.menu.scientific_metadata?.bmi_current && (
                      <div className="nutritionalInfoItem">
                        <div className="nutritionalInfoLabel">IMC Atual</div>
                        <div className="nutritionalInfoValue">
                          {menuData.menu.scientific_metadata.bmi_current.toFixed(
                            1
                          )}
                        </div>
                      </div>
                    )}
                    {menuData.menu.scientific_metadata?.bmi_target && (
                      <div className="nutritionalInfoItem">
                        <div className="nutritionalInfoLabel">IMC Alvo</div>
                        <div className="nutritionalInfoValue">
                          {menuData.menu.scientific_metadata.bmi_target.toFixed(
                            1
                          )}
                        </div>
                      </div>
                    )}
                    {menuData.menu.scientific_metadata?.geb_kcal && (
                      <div className="nutritionalInfoItem">
                        <div className="nutritionalInfoLabel">GEB</div>
                        <div className="nutritionalInfoValue">
                          {Math.round(
                            menuData.menu.scientific_metadata.geb_kcal
                          )}{' '}
                          kcal
                        </div>
                        <div className="nutritionalInfoSubValue">
                          (Gasto Energético Basal)
                        </div>
                      </div>
                    )}
                    {menuData.menu.scientific_metadata?.naf_factor && (
                      <div className="nutritionalInfoItem">
                        <div className="nutritionalInfoLabel">NAF</div>
                        <div className="nutritionalInfoValue">
                          {menuData.menu.scientific_metadata.naf_factor.toFixed(
                            2
                          )}
                        </div>
                        <div className="nutritionalInfoSubValue">
                          (Nível de Atividade Física)
                        </div>
                      </div>
                    )}
                  </div>
                  {menuData.menu.macros && (
                    <div>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#6b7280',
                          marginBottom: '8px',
                        }}
                      >
                        Macronutrientes
                      </div>
                      <div className="macrosGrid">
                        <div className="macroItem">
                          <div className="macroLabel">Proteínas</div>
                          <div className="macroValue">
                            {Math.round(menuData.menu.macros.protein_g)}g
                          </div>
                        </div>
                        <div className="macroItem">
                          <div className="macroLabel">Carboidratos</div>
                          <div className="macroValue">
                            {Math.round(menuData.menu.macros.carbs_g)}g
                          </div>
                        </div>
                        <div className="macroItem">
                          <div className="macroLabel">Gorduras</div>
                          <div className="macroValue">
                            {Math.round(menuData.menu.macros.fat_g)}g
                          </div>
                        </div>
                        <div className="macroItem">
                          <div className="macroLabel">Total</div>
                          <div className="macroValue">
                            {Math.round(menuData.menu.macros.calories_kcal)}{' '}
                            kcal
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </S.NutritionalInfo>
              )}

              <div className="foodMenu">
                <div className="foodMenuItem">
                  <p className="foodMenuTitle">
                    {translate('medicalRecord.liquids')}
                  </p>
                  <p className="foodMenuText">
                    {menuData?.menu?.orientacoes_nutricionais?.liquidos || ''}
                  </p>
                </div>
                <div className="foodMenuItem">
                  <p className="foodMenuTitle">
                    {translate('medicalRecord.supplements')}
                  </p>
                  <p className="foodMenuText">
                    {menuData?.menu?.orientacoes_nutricionais?.suplementos ||
                      ''}
                  </p>
                </div>
              </div>

              {menuData?.menu?.refeicoes?.map(
                (refeicao: Refeicao, index: number) => (
                  <div key={index} className="breakfast">
                    <div className="breakfastTitle">
                      <div className="breakfastTitleContent">
                        <p className="breakfastTitleText">{refeicao.titulo}</p>
                        <p className="breakfastTitleSubText">
                          {refeicao.instrucoes}
                        </p>
                      </div>
                      {!isPlanSent && (
                        <button
                          className="regenerateButton"
                          onClick={() =>
                            handleRegenerate(refeicao.tipo || refeicao.titulo)
                          }
                          disabled={isRegenerating}
                          title="Regerar plano alimentar"
                        >
                          <span>Regerar</span>
                          <img
                            src={regenerateIcon}
                            alt="Regerar"
                            style={{
                              marginLeft: '6px',
                              width: '16px',
                              height: '16px',
                            }}
                          />
                        </button>
                      )}
                    </div>
                    <div className="breakfastContent">
                      {refeicao.opcoes.map(
                        (opcao: OpcaoAlimentar, opcaoIndex: number) => (
                          <div
                            key={opcaoIndex}
                            className="breakfastContentItem"
                          >
                            <div className="breakfastContentItemContent">
                              <p className="breakfastContentItemText">
                                • {opcao.descricao}
                              </p>
                              <div className="breakfastContentItemSubText">
                                {opcao.porcao}
                              </div>
                            </div>
                            {opcao.alternativas.length > 0 && (
                              <div className="breakfastContentItemTextSecondary">
                                {opcao.alternativas.map(
                                  (alternativa: string, altIndex: number) => (
                                    <p key={altIndex} style={{ margin: 0 }}>
                                      {alternativa}
                                    </p>
                                  )
                                )}
                              </div>
                            )}
                            {opcao.observacoes && (
                              <p
                                className="breakfastContentItemTextSecondary"
                                style={{
                                  marginTop: '4px',
                                  fontStyle: 'italic',
                                }}
                              >
                                {translate('medicalRecord.observations')}:{' '}
                                {opcao.observacoes}
                              </p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )
              )}

              <S.PlanIncluded>
                <div className="planIncludedHeader">
                  <h4>{translate('medicalRecord.includedInPlan')}</h4>
                </div>

                <div className="planIncludedBody">
                  {menuData?.menu?.incluso_plano?.orientacoes &&
                    menuData.menu.incluso_plano.orientacoes.length > 0 && (
                      <div>
                        <h4 style={{ marginTop: 0 }}>
                          {translate('medicalRecord.guidelines')}
                        </h4>
                        <ul className="planIncludedList">
                          {menuData.menu.incluso_plano.orientacoes.map(
                            (orientacao: string, index: number) => (
                              <li key={index}>{orientacao}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  {menuData?.menu?.incluso_plano?.observacoes &&
                    menuData.menu.incluso_plano.observacoes.length > 0 && (
                      <div>
                        <h4 style={{ marginTop: 0 }}>
                          {translate('medicalRecord.observations')}
                        </h4>
                        <ul className="planIncludedList">
                          {menuData.menu.incluso_plano.observacoes.map(
                            (observacao: string, index: number) => (
                              <li key={index}>{observacao}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  {menuData?.menu?.how_to_follow && (
                    <div>
                      <h4 style={{ marginTop: 0 }}>
                        {translate('medicalRecord.planningTips')}
                      </h4>
                      <ul className="planIncludedList">
                        {menuData.menu.how_to_follow
                          .split('\n')
                          .filter((dica) => dica.trim() !== '')
                          .map((dica: string, index: number) => (
                            <li key={index}>{dica}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              </S.PlanIncluded>

              {menuData?.menu?.adaptacoes_realizadas &&
                menuData.menu.adaptacoes_realizadas.length > 0 &&
                menuData.menu.adaptacoes_realizadas.some(
                  (adaptacao) => adaptacao.trim() !== ''
                ) && (
                  <div style={{ marginTop: '20px' }}>
                    <h4>{translate('medicalRecord.adaptationsMade')}</h4>
                    <ul>
                      {menuData.menu.adaptacoes_realizadas
                        .filter((adaptacao) => adaptacao.trim() !== '')
                        .map((adaptacao: string, index: number) => (
                          <li key={index} style={{ marginBottom: '8px' }}>
                            {adaptacao}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
            </>
          )}
        </>
      )}

      {edit && menuData && (
        <FoodMenuEdit
          setEdit={setEdit}
          menuData={menuData}
          onSave={handleSave}
        />
      )}
    </S.SectionNutri>
  )
}

export default FoodMenu
